import { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import type { RecorderCallbacks, SummaryResult } from '@chewy/audio-to-notes-ui'
import { v4 as uuid } from 'uuid'

import SnackNotificationType from '~/constants/SnackNotificationType'
import {
  clearRecordingContext,
  getRecordingCorrelationId,
  getRecordingDischargeNotes,
  getRecordingMedicalNotes,
  getRecordingPatientId,
  getRecordingSoapId,
  setInitializingSession,
  setRecording,
  setRecordingCorrelationId,
  setRecordingDischargeNotes,
  setRecordingMedicalNotes,
  setRecordingPatientId,
  setRecordingSoapId,
  setSummarizing,
} from '~/store/duck/audioRecorder'
import { setPendingInjection } from '~/store/duck/audioRecorderInjection'
import { addUiNotification } from '~/store/duck/uiNotifications'
import { getPatientName } from '~/store/reducers/patients'
import {
  getDischargeNotes,
  getMedicalNotes,
  getPatientId,
  getSoapId,
} from '~/store/reducers/soap'

import type { RecordNavigationVariant } from '../modals/RecordNavigationModal'
import { determineSummaryDestination } from './summaryHandling'
import { useSummaryContext } from './useSummaryContext'

export interface AudioRecorderModalHandlers {
  onOpenMicPermissionModal: () => void
  onOpenNoAudioModal: (callbacks: {
    onContinueRecording?: () => void
    onEndRecording?: () => void
  }) => void
  onOpenRecordNavigationModal: (params: {
    currentPatientName?: string
    onCancel: () => void
    onContinueRecording: () => void
    onSwitchRecording?: () => void
    recordingPatientName: string
    variant: RecordNavigationVariant
  }) => void
  onOpenSummarizeNavigationModal: (params: {
    onCancel: () => void
    onConfirm: () => void
    patientName: string
  }) => void
}

export const useAudioRecorderCallbacks = (
  modalHandlers: AudioRecorderModalHandlers,
): RecorderCallbacks => {
  const dispatch = useDispatch()
  const store = useStore()
  const navigate = useNavigate()
  const { t } = useTranslation(['Common', 'Errors'])
  const context = useSummaryContext()
  const medicalNotes = useSelector(getMedicalNotes)
  const dischargeNotes = useSelector(getDischargeNotes)
  const patientId = useSelector(getPatientId)
  const patientName = useSelector(getPatientName(patientId))
  const recordingCorrelationId = useSelector(getRecordingCorrelationId)
  const recordingPatientId = useSelector(getRecordingPatientId)
  const recordingSoapId = useSelector(getRecordingSoapId)
  const recordingMedicalNotes = useSelector(getRecordingMedicalNotes)
  const recordingDischargeNotes = useSelector(getRecordingDischargeNotes)
  const recordingPatientName = useSelector(getPatientName(recordingPatientId))
  const currentSoapId = useSelector(getSoapId)

  useEffect(() => {
    if (context.isOnSoapPage && currentSoapId && !recordingSoapId) {
      dispatch(setRecordingSoapId(currentSoapId))
    }
  }, [context.isOnSoapPage, currentSoapId, recordingSoapId, dispatch])

  useEffect(() => {
    if (recordingSoapId && currentSoapId === recordingSoapId) {
      dispatch(setRecordingMedicalNotes(medicalNotes ?? ''))
      dispatch(setRecordingDischargeNotes(dischargeNotes ?? ''))
    }
  }, [recordingSoapId, currentSoapId, medicalNotes, dischargeNotes, dispatch])

  const handleBeforeRecord = useCallback(
    async (_params: {
      appName: string
      correlationId: string
      recordId: string | null
      sessionId: string | null
    }): Promise<boolean | 'cancel'> => {
      const hasActiveRecording = recordingCorrelationId != null
      if (!hasActiveRecording) return true

      const { isOnSoapPage } = context
      const isOnRecordingSoap = currentSoapId === recordingSoapId

      // Patient name is always available at runtime (patients map loads before
      // recorder is usable, recordingPatientId is persisted in Redux). The ?? ''
      // is a TypeScript safety net for getPatientName's string | undefined return.
      if (!recordingPatientName) {
        // eslint-disable-next-line no-console
        console.error(
          '[useAudioRecorderCallbacks] recordingPatientName unexpectedly missing',
        )
      }

      if (!isOnSoapPage) {
        return new Promise<boolean>((resolve) => {
          modalHandlers.onOpenRecordNavigationModal({
            variant: 'non-soap',
            recordingPatientName: recordingPatientName ?? '',
            onCancel: () => resolve(false),
            onContinueRecording: () => resolve(true),
          })
        })
      }

      if (isOnSoapPage && !isOnRecordingSoap) {
        return new Promise<boolean | 'cancel'>((resolve) => {
          modalHandlers.onOpenRecordNavigationModal({
            variant: 'different-soap',
            recordingPatientName: recordingPatientName ?? '',
            currentPatientName: patientName ?? '',
            onCancel: () => resolve(false),
            onContinueRecording: () => resolve(true),
            onSwitchRecording: () => {
              dispatch(clearRecordingContext())
              resolve('cancel')
            },
          })
        })
      }

      return true
    },
    [
      recordingCorrelationId,
      recordingSoapId,
      currentSoapId,
      recordingPatientName,
      patientName,
      context.isOnSoapPage,
      modalHandlers,
      dispatch,
    ],
  )

  const buildTranscriptData = useCallback(() => {
    const useRecordingNotes =
      recordingCorrelationId != null &&
      recordingSoapId != null &&
      currentSoapId !== recordingSoapId
    const medicalNotesForTranscript: string = useRecordingNotes
      ? (recordingMedicalNotes ?? '')
      : (medicalNotes ?? '')
    const dischargeNotesForTranscript: string = useRecordingNotes
      ? (recordingDischargeNotes ?? '')
      : (dischargeNotes ?? '')
    return [
      {
        summary: { type: 'soap' as const },
        transcript: { text: medicalNotesForTranscript },
      },
      {
        summary: { type: 'discharge' as const },
        transcript: { text: dischargeNotesForTranscript },
      },
    ]
  }, [
    medicalNotes,
    dischargeNotes,
    recordingCorrelationId,
    recordingSoapId,
    currentSoapId,
    recordingMedicalNotes,
    recordingDischargeNotes,
  ])

  const handleBeforeSummarize = useCallback(
    async (_params: {
      appName: string
      correlationId: string
      templates?: Record<string, string>
    }) => {
      const isOnDifferentPage =
        recordingCorrelationId != null &&
        recordingSoapId != null &&
        (!context.isOnSoapPage || currentSoapId !== recordingSoapId)

      if (isOnDifferentPage) {
        if (!recordingPatientName) {
          // eslint-disable-next-line no-console
          console.error(
            '[useAudioRecorderCallbacks] recordingPatientName unexpectedly missing in summarize flow',
          )
        }
        return new Promise<ReturnType<typeof buildTranscriptData> | null>(
          (resolve) => {
            modalHandlers.onOpenSummarizeNavigationModal({
              patientName: recordingPatientName ?? '',
              onCancel: () => resolve(null),
              onConfirm: () => {
                navigate(`/soap/${recordingSoapId}`)
                resolve(buildTranscriptData())
              },
            })
          },
        )
      }

      return buildTranscriptData()
    },
    [
      recordingCorrelationId,
      recordingSoapId,
      currentSoapId,
      recordingPatientName,
      modalHandlers,
      buildTranscriptData,
      navigate,
      context.isOnSoapPage,
    ],
  )

  // State tracking callbacks
  const handleRecordingStateChange = useCallback(
    (data: {
      appName: string
      correlationId: string
      isRecording: boolean
      recordId: string | null
    }) => {
      try {
        // Always update state - callbacks are source of truth
        // Redux state reflects actual recording state, not current page context
        dispatch(setRecording(data.isRecording))

        // Capture recording context when recording starts — locks to the originating SOAP.
        // Persists across navigation so summarize API and transcript use the correct data.
        if (data.isRecording && context.soapId) {
          dispatch(setRecordingCorrelationId(data.correlationId))
          dispatch(setRecordingSoapId(context.soapId))
          const currentPatientId = getPatientId(store.getState())
          dispatch(setRecordingPatientId(currentPatientId ?? null))
          const medical = getMedicalNotes(store.getState())
          const discharge = getDischargeNotes(store.getState())
          dispatch(setRecordingMedicalNotes(medical ?? ''))
          dispatch(setRecordingDischargeNotes(discharge ?? ''))
        } else if (!data.isRecording) {
          // Keep recording context for summarization and lock display.
          // Cleared after successful summarization (same SOAP) or in handleSessionChanged.
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onRecordingStateChange', error)
        // Still update state to prevent stuck state
        dispatch(setRecording(false))
      }
    },
    [dispatch, context.soapId, store],
  )

  const handleSummarizationStateChange = useCallback(
    (data: {
      appName: string
      correlationId: string
      isSummarizing: boolean
      recordId: string | null
    }) => {
      try {
        dispatch(setSummarizing(data.isSummarizing))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onSummarizationStateChange', error)
        dispatch(setSummarizing(false))
      }
    },
    [dispatch],
  )

  // onSessionChanged
  const handleSessionChanged = useCallback(
    (_data: {
      appName: string
      newCorrelationId: string
      newSessionId: string | null
      oldCorrelationId: string
      oldSessionId: string | null
    }) => {
      try {
        dispatch(setRecording(false))
        dispatch(setSummarizing(false))
        dispatch(setInitializingSession(false))
        dispatch(clearRecordingContext())
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onSessionChanged', error)
        dispatch(setRecording(false))
        dispatch(setSummarizing(false))
        dispatch(setInitializingSession(false))
        dispatch(clearRecordingContext())
      }
    },
    [dispatch],
  )

  // onCorrelationIdChange
  const handleCorrelationIdChange = useCallback(
    (data: {
      appName: string
      currentState: string
      isRecording: boolean
      isSummarizing: boolean
      newCorrelationId: string
      oldCorrelationId: string
      recordId: string | null
    }) => {
      // eslint-disable-next-line no-console
      console.error('Unexpected correlationId change', data)
    },
    [],
  )

  // onComponentUnmount
  const handleComponentUnmount = useCallback(
    (_data: {
      appName: string
      correlationId: string
      hasActiveRecording: boolean
      hasActiveSummarization: boolean
      lifecycleState: string
      recordId: string | null
    }) => {
      // Reset initialization state on unmount
      dispatch(setInitializingSession(false))
      // Optional: Cleanup Portal-specific state
      // Library handles its own cleanup via config
    },
    [dispatch],
  )

  // onSummarizationStart
  const handleSummarizationStart = useCallback(
    (_data: {
      appName: string
      correlationId: string
      recordId: string | null
    }) => {
      // No-op: Previously captured patient name for mismatch modal, but modals removed
    },
    [],
  )

  // onSessionInit
  const handleSessionInit = useCallback(
    (_data: {
      appName: string
      correlationId: string
      hasRecording: boolean
      hasSummary: boolean
    }) => {
      // No-op: recordingSoapId is now set in onRecordingStateChange when recording starts
      // This prevents recordingSoapId from being overwritten when user navigates to different SOAP
    },
    [],
  )

  // onSummarizationComplete
  // Dispatches pending injection to Redux — CKEditor's Model API (via useAudioSummaryInjection)
  // is the single write path for SOAP note persistence through the collaboration layer.
  // No GraphQL mutation needed; CKEditor onChange → collaboration sync handles backend writes.
  const handleSummarizationComplete = useCallback(
    (data: {
      appName: string
      correlationId: string
      recordId: string | null
      summaries: SummaryResult[]
    }) => {
      try {
        dispatch(setSummarizing(false))

        const { summaries, correlationId } = data

        const destination = determineSummaryDestination(summaries, {
          pathname: context.pathname,
          currentAppointmentId: context.currentAppointmentId ?? null,
          isOnSoapPage: context.isOnSoapPage,
          summariesFTEnabled: context.summariesFTEnabled,
          correlationId,
        })

        if (destination.type === 'discard') {
          dispatch(clearRecordingContext())
          return
        }

        if (destination.type === 'queued' || destination.type === 'inject') {
          const capturedSoapId = getRecordingSoapId(store.getState())

          const medicalSummary = destination.summaries.find(
            (s: SummaryResult) => s.summaryType === 'soap',
          )
          const dischargeSummary = destination.summaries.find(
            (s: SummaryResult) => s.summaryType === 'discharge',
          )
          const medicalNotesForInjection = medicalSummary?.summaryText || null
          const dischargeNotesForInjection =
            dischargeSummary?.summaryText || null

          const hasContent =
            medicalNotesForInjection || dischargeNotesForInjection

          if (!capturedSoapId || !hasContent) {
            // eslint-disable-next-line no-console
            console.warn(
              '[AudioRecorder] Skipping injection — missing soapId or empty summaries',
              {
                capturedSoapId,
                medicalNotesForInjection,
                dischargeNotesForInjection,
              },
            )
            return
          }

          dispatch(
            setPendingInjection({
              soapId: capturedSoapId,
              medicalNotes: medicalNotesForInjection,
              dischargeNotes: dischargeNotesForInjection,
            }),
          )

          const isOnPage = destination.type === 'inject'
          const toastMessage = isOnPage
            ? t('Common:AUDIO_RECORDER.NOTES_UPDATED_SUCCESSFULLY')
            : t('Common:AUDIO_RECORDER.SUMMARY_COMPLETED')
          const toastType = isOnPage
            ? SnackNotificationType.AUDIO_RECORDER_NOTES_UPDATED
            : SnackNotificationType.AUDIO_RECORDER_SUMMARY_COMPLETED
          dispatch(
            addUiNotification({
              id: uuid(),
              message: toastMessage,
              type: toastType,
              params: {
                soapId: capturedSoapId,
              },
            }),
          )
          // Clear recording context when summarization completes on the recording SOAP.
          // With navigation modals, the user is always routed back before summarizing,
          // so this path runs and releases the correlation lock for the next session.
          if (
            context.currentAppointmentId != null &&
            context.currentAppointmentId === correlationId
          ) {
            dispatch(clearRecordingContext())
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in onSummarizationComplete', error)
        dispatch(clearRecordingContext())
        dispatch(setSummarizing(false))
        dispatch(
          addUiNotification({
            id: uuid(),
            message: t('Errors:AUDIO_RECORDER.ERROR_PROCESSING_SUMMARY'),
            type: SnackNotificationType.ERROR,
          }),
        )
      }
    },
    [dispatch, t, context, store],
  )

  const getLockHolderName = useCallback(() => {
    const onRecordingSoap =
      recordingSoapId == null || recordingSoapId === currentSoapId
    const name = onRecordingSoap ? patientName : recordingPatientName
    return name || undefined
  }, [patientName, recordingPatientName, recordingSoapId, currentSoapId])

  // Modal callbacks
  const handleMicrophoneError = useCallback(
    (data: {
      appName: string
      correlationId: string
      error: Error
      errorCode: string
      recordId: string | null
    }) => {
      if (data.errorCode === 'PERMISSION_DENIED') {
        // Modal shows instructions on how to enable microphone access
        // Browser security prevents programmatically opening device settings
        modalHandlers.onOpenMicPermissionModal()
      }
    },
    [modalHandlers],
  )

  const handleNoAudioReceived = useCallback(
    (_data: {
      appName: string
      correlationId: string
      recordId: string | null
    }) => {
      modalHandlers.onOpenNoAudioModal({
        onContinueRecording: () => {
          // Intentionally empty - modal dismisses via onClose()
          // User can continue recording via library's AudioController UI
          // The library handles recording state internally
        },
        onEndRecording: () => {
          // TODO(SCRIBE-144): Implement stop recording without summarizing
          // Currently no way to programmatically stop recording from Portal callbacks
          // The library's AudioController handles recording control via its own UI
          // For now, modal dismisses via onClose() and user must stop via library UI
        },
      })
    },
    [modalHandlers],
  )

  // onError - Show "Doggo" error notification when status API or other errors occur
  const handleError = useCallback(
    (data: {
      appName: string
      correlationId: string
      error: Error
      recordId: string | null
    }) => {
      // eslint-disable-next-line no-console
      console.error('Audio Recorder Error:', {
        message: data.error.message,
        appName: data.appName,
        correlationId: data.correlationId,
        recordId: data.recordId,
      })

      // Show "Doggo" error notification (generic error for status API failures, etc.)
      dispatch(
        addUiNotification({
          id: uuid(),
          message: t('Errors:AUDIO_RECORDER.ERROR_PROCESSING_SUMMARY'), // TODO: Add specific ERROR_GENERIC translation key
          type: SnackNotificationType.ERROR,
        }),
      )
    },
    [dispatch, t],
  )

  return {
    onBeforeRecord: handleBeforeRecord,
    onBeforeSummarize: handleBeforeSummarize,
    onRecordingStateChange: handleRecordingStateChange,
    onSummarizationStateChange: handleSummarizationStateChange,
    onSessionChanged: handleSessionChanged,
    onCorrelationIdChange: handleCorrelationIdChange,
    onComponentUnmount: handleComponentUnmount,
    onSessionInit: handleSessionInit,
    onSummarizationStart: handleSummarizationStart,
    onSummarizationComplete: handleSummarizationComplete,
    getLockHolderName,
    onMicrophoneError: handleMicrophoneError,
    onNoAudioReceived: handleNoAudioReceived,
    onError: handleError,
    // Note: Add onRecordingTimeout when library supports it
  }
}
